/**
 * i18n setup — react-intl + locale matching + persistencia en localStorage.
 */
import { match } from "@formatjs/intl-localematcher";

import { messages as esCL } from "./messages/es-CL";
import { messages as esAR } from "./messages/es-AR";
import { messages as esMX } from "./messages/es-MX";

export const SUPPORTED_LOCALES = ["es-CL", "es-AR", "es-MX"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: SupportedLocale = "es-CL";

const MESSAGES: Record<SupportedLocale, Record<string, string>> = {
  "es-CL": esCL,
  "es-AR": esAR,
  "es-MX": esMX,
};

const STORAGE_KEY = "simumed.locale";

export function detectLocale(): SupportedLocale {
  // 1. explícito en localStorage (user switcher)
  if (typeof window !== "undefined") {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved && (SUPPORTED_LOCALES as readonly string[]).includes(saved)) {
      return saved as SupportedLocale;
    }
  }
  // 2. navigator.languages (BCP-47 matching)
  if (typeof navigator !== "undefined") {
    const pref = Array.from(navigator.languages ?? [navigator.language]);
    try {
      return match(pref, SUPPORTED_LOCALES as unknown as string[], DEFAULT_LOCALE) as SupportedLocale;
    } catch {
      return DEFAULT_LOCALE;
    }
  }
  return DEFAULT_LOCALE;
}

export function setLocale(locale: SupportedLocale): void {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(STORAGE_KEY, locale);
  }
}

export function getMessages(locale: SupportedLocale): Record<string, string> {
  return MESSAGES[locale] ?? MESSAGES[DEFAULT_LOCALE];
}

export const LOCALE_LABELS: Record<SupportedLocale, string> = {
  "es-CL": "Español (Chile)",
  "es-AR": "Español (Argentina)",
  "es-MX": "Español (México)",
};
